import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { DjangoTokenResponse, useAuthStore } from "@/store/auth";

export const API_URL = __IS_DEV__ ? "/api" : `${__API_URL__}/api`;

const $api: AxiosInstance = axios.create({
  withCredentials: true,
  baseURL: API_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error: any) => void;
  config: AxiosRequestConfig;
}> = [];

const processQueue = (
  error: any,
  tokenResponse: DjangoTokenResponse | null = null
) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else if (tokenResponse) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${tokenResponse.access}`;
      resolve($api(config));
    }
  });
  failedQueue = [];
};

$api.interceptors.request.use((config) => {
  const access = useAuthStore.getState().getAccessToken();
  if (access) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

$api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    const originalRequest = err.config!;
    const store = useAuthStore.getState();

    if (err.response?.status !== 401 || (originalRequest as any)._retry) {
      if (err.response?.status === 401) {
        store.clearAuth();
      }
      return Promise.reject(err);
    }

    (originalRequest as any)._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    return new Promise(async (resolve, reject) => {
      try {
        const currentRefresh = store.tokenResponse?.refresh;
        if (!currentRefresh) throw new Error("No refresh token available");

        const { data } = await axios.post<{ access: string; refresh: string }>(
          `${API_URL}/auth/jwt/refresh/`,
          { refresh: currentRefresh },
          { withCredentials: true }
        );

        const newTokenResponse: DjangoTokenResponse = {
          ...store.tokenResponse!,
          access: data.access,
          refresh: data.refresh,
        };

        store.setTokenResponse(newTokenResponse);

        processQueue(null, newTokenResponse);

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
        resolve($api(originalRequest));
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.clearAuth();
        reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    });
  }
);

export default $api;
